'use client';

import { useState } from 'react';
import { Users, Plus, X, Heart } from 'lucide-react';
import { PageHeader } from '@/components/shared/page-header';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  useDirectorParents,
  useParentChildren,
  useDirectorPlayers,
  useLinkChild,
  useUnlinkChild,
} from '@/hooks/use-director-data';
import type { ParentSummary } from '@/hooks/use-director-data';

export default function DirectorParentsPage() {
  const [selectedParent, setSelectedParent] = useState<ParentSummary | null>(null);
  const [linkModalOpen, setLinkModalOpen] = useState(false);
  const [selectedPlayerId, setSelectedPlayerId] = useState('');

  const { data: parentsData, isLoading: parentsLoading } = useDirectorParents();
  const { data: childrenData, isLoading: childrenLoading } = useParentChildren(
    selectedParent?.id || ''
  );
  const { data: playersData } = useDirectorPlayers();

  const linkChild = useLinkChild();
  const unlinkChild = useUnlinkChild();

  const handleLinkChild = async () => {
    if (selectedParent && selectedPlayerId) {
      await linkChild.mutateAsync({
        parentId: selectedParent.id,
        playerId: selectedPlayerId,
      });
      setSelectedPlayerId('');
      setLinkModalOpen(false);
    }
  };

  const handleUnlinkChild = async (playerId: string) => {
    if (selectedParent) {
      await unlinkChild.mutateAsync({
        parentId: selectedParent.id,
        playerId,
      });
    }
  };

  // Get players not already linked to this parent
  const availablePlayers = playersData?.players.filter(
    (p) => !childrenData?.children.some((cp) => cp.id === p.id)
  ) || [];

  return (
    <div className="space-y-6">
      <PageHeader title="Parents" subtitle="Manage parent/guardian-player relationships" />

      <div className="grid md:grid-cols-2 gap-6">
        {/* Parent List */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Heart className="w-5 h-5" />
              All Parents
            </CardTitle>
          </CardHeader>
          <CardContent>
            {parentsLoading ? (
              <div className="space-y-3">
                {[1, 2, 3].map((i) => (
                  <Skeleton key={i} className="h-16 w-full" />
                ))}
              </div>
            ) : parentsData?.parents.length === 0 ? (
              <p className="text-muted-foreground text-center py-4">
                No parents in your club yet.
              </p>
            ) : (
              <div className="space-y-2">
                {parentsData?.parents.map((parent) => (
                  <button
                    key={parent.id}
                    onClick={() => setSelectedParent(parent)}
                    className={`w-full p-3 rounded-lg border text-left transition ${
                      selectedParent?.id === parent.id
                        ? 'border-purple-500 bg-purple-50 dark:bg-purple-950/30'
                        : 'border-border hover:border-border hover:bg-accent'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-green-500 to-teal-500 flex items-center justify-center text-white font-bold">
                          {parent.email[0].toUpperCase()}
                        </div>
                        <div>
                          <p className="font-medium text-foreground">
                            {parent.email.split('@')[0]}
                          </p>
                          <p className="text-sm text-muted-foreground">{parent.email}</p>
                        </div>
                      </div>
                      <Badge variant="secondary">
                        {parent.childrenCount} {parent.childrenCount === 1 ? 'player' : 'players'}
                      </Badge>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Linked Players */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Users className="w-5 h-5" />
                {selectedParent
                  ? `${selectedParent.email.split('@')[0]}'s Players`
                  : 'Select a Parent/Guardian'}
              </CardTitle>
              {selectedParent && (
                <Button
                  size="sm"
                  onClick={() => setLinkModalOpen(true)}
                  className="bg-purple-600 hover:bg-purple-700"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Link Player
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {!selectedParent ? (
              <p className="text-muted-foreground text-center py-8">
                Select a parent/guardian to view their linked players
              </p>
            ) : childrenLoading ? (
              <div className="space-y-3">
                {[1, 2].map((i) => (
                  <Skeleton key={i} className="h-14 w-full" />
                ))}
              </div>
            ) : childrenData?.children.length === 0 ? (
              <p className="text-muted-foreground text-center py-8">
                No players linked to this parent/guardian yet.
              </p>
            ) : (
              <div className="space-y-2">
                {childrenData?.children.map((player) => (
                  <div
                    key={player.id}
                    className="flex items-center justify-between p-3 bg-muted rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-indigo-500 flex items-center justify-center text-white text-sm font-bold">
                        {player.firstName[0]}
                        {player.lastName[0]}
                      </div>
                      <div>
                        <p className="font-medium text-foreground">
                          {player.firstName} {player.lastName}
                        </p>
                        {player.skillLevel && (
                          <p className="text-sm text-muted-foreground capitalize">
                            {player.skillLevel}
                          </p>
                        )}
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleUnlinkChild(player.id)}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Link Child Modal */}
      <Dialog open={linkModalOpen} onOpenChange={setLinkModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Link Player to {selectedParent?.email.split('@')[0]}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {availablePlayers.length === 0 ? (
              <p className="text-muted-foreground text-center">
                All players are already linked to this parent.
              </p>
            ) : (
              <>
                <Select value={selectedPlayerId} onValueChange={setSelectedPlayerId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a player" />
                  </SelectTrigger>
                  <SelectContent>
                    {availablePlayers.map((player) => (
                      <SelectItem key={player.id} value={player.id}>
                        {player.firstName} {player.lastName}
                        {player.skillLevel && ` (${player.skillLevel})`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={() => setLinkModalOpen(false)}>
                    Cancel
                  </Button>
                  <Button
                    onClick={handleLinkChild}
                    disabled={!selectedPlayerId || linkChild.isPending}
                    className="bg-purple-600 hover:bg-purple-700"
                  >
                    {linkChild.isPending ? 'Linking...' : 'Link Player'}
                  </Button>
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
